import { useCallback } from 'react';

export type SelectionMode = 'single' | 'range';

type UseYearSelectionProps = {
  selectedYears: number[];
  selectionMode: SelectionMode;
  onSelectionChange: (years: number[]) => void;
};

type YearSelectionState = {
  isSelected: (year: number) => boolean;
  isInRange: (year: number) => boolean;
  handleYearClick: (year: number) => void;
};

export const useYearSelection = ({
  selectedYears,
  selectionMode,
  onSelectionChange,
}: UseYearSelectionProps): YearSelectionState => {
  const sortedSelected = [...selectedYears].sort((a, b) => a - b);
  const rangeStart = sortedSelected.length > 0 ? sortedSelected[0] : null;
  const rangeEnd = sortedSelected.length > 1 ? sortedSelected[sortedSelected.length - 1] : null;

  const isSelected = useCallback(
    (year: number) => {
      if (selectionMode === 'single') {
        return selectedYears.includes(year);
      }

      if (rangeEnd !== null && rangeStart !== null) {
        return year === rangeStart || year === rangeEnd;
      }

      return rangeStart === year;
    },
    [selectionMode, selectedYears, rangeStart, rangeEnd],
  );

  const isInRange = useCallback(
    (year: number) => {
      if (selectionMode === 'single') {
        return false;
      }

      if (rangeStart !== null && rangeEnd !== null) {
        return year > rangeStart && year < rangeEnd;
      }

      return false;
    },
    [selectionMode, rangeStart, rangeEnd],
  );

  const handleYearClick = useCallback(
    (year: number) => {
      if (selectionMode === 'single') {
        const newSelection = selectedYears.includes(year) ? [] : [year];

        onSelectionChange(newSelection);
      } else {
        // Range selection
        if (selectedYears.length === 1 && selectedYears[0] === year) {
          onSelectionChange([]);
        } else if (selectedYears.length === 0 || selectedYears.length === 2) {
          onSelectionChange([year]);
        } else {
          const sorted = [selectedYears[0], year].sort((a, b) => a - b);

          onSelectionChange(sorted);
        }
      }
    },
    [selectionMode, selectedYears, onSelectionChange],
  );

  return {
    isSelected,
    isInRange,
    handleYearClick,
  };
};
